'use strict';

const axios = require('axios');
const {validateElement, softValidateElementAttributes} = require('../../../vue-form/vue-input/libs/validator');

const ssnMapping = {
    firstname: 'firstName',
    lastname: 'lastName',
    company: 'companyName',
    address: 'street',
    streetnumber: 'houseNumber',
    postalCode: 'postalCode',
    area: 'city',
    mobileNumber: 'phone'
};
const rgcMapping = {
    street: 'street',
    city: 'city',
    mobileNumber: 'phone'
};
module.exports = (detailsData) => ({
    namespaced: true,
    state: {
        ...detailsData
    },
    getters: {
        isShippingValid(state) {
            const elements = state.forms.shippingAddress.formElements;
            return elements.reduce(
                (result, element) => result && !validateElementInFormContext(element, elements).length,
                true
            );
        },
        isCompanyDetailsValid(state) {
            const elements = state.forms.companydetails.formElements;
            return elements.reduce(
                (result, element) => result && !validateElementInFormContext(element, elements).length,
                true
            );
        },
        firstInvalidShippingElementId(state) {
            const elements = state.forms.shippingAddress.formElements;
            const invalidElement = elements.find((element) => validateElementInFormContext(element, elements).length);

            return invalidElement.attributes.formId;
        },
        firstInvalidCompanyElementId(state) {
            const elements = state.forms.companydetails.formElements;
            const invalidElement = elements.find((element) => validateElementInFormContext(element, elements).length);

            return invalidElement.attributes.formId;
        },
        firstInvalidBillingElementId(state) {
            const elements = state.forms.billingAddress.formElements;
            const sameAsDeliveryAddress = state.forms.billingAddress.formElements[0];
            if (sameAsDeliveryAddress.state.value) {
                const vatElement = elements.find(
                    (element) => element.id === 'btwVatNumber' || element.id === 'nifNumber'
                );
                if (vatElement && validateElementInFormContext(vatElement, elements).length) {
                    return vatElement.attributes.formId;
                }
            }

            const invalidElement = elements.find((element) => validateElementInFormContext(element, elements).length);

            return invalidElement.attributes.formId;
        },
        isBillingValid(state) {
            const elements = state.forms.billingAddress.formElements;
            const sameAsDeliveryAddress = state.forms.billingAddress.formElements[0];
            if (sameAsDeliveryAddress.state.value) {
                const vatElement = elements.find(
                    (element) => element.id === 'btwVatNumber' || element.id === 'nifNumber'
                );
                if (vatElement) {
                    return !validateElementInFormContext(vatElement, elements).length;
                }
                return true;
            }

            return elements.reduce(
                (result, element) => result && !validateElementInFormContext(element, elements).length,
                true
            );
        },
        isFormValid: (_, getters) => (formId) => (formId === 'shippingAddress' ? getters.isShippingValid : getters.isBillingValid),
        isFieldValid: (state) => (formId, fieldId) => {
            const form = state.forms[formId];
            const formElements = form ? form.formElements : state.forms.formElements;
            const formElement = formElements.find((element) => element.id === fieldId);
            if (!formElement) {
                return true;
            }
            return !validateElementInFormContext(formElement, formElements).length;
        },
        shippingPostalCodeValue: (state) =>
            state.forms.shippingAddress.formElements.find((element) => element.id === 'postalCode').state.value,
        billingPostalCodeValue: (state) =>
            state.forms.billingAddress.formElements.find((element) => element.id === 'postalCode').state.value,
        getCityHKValue: (state) => (formId) => {
            if (!formId) {
                return;
            }
            return state.forms[formId].formElements.find((element) => element.id === 'city').state.value;
        },
        getDistrictHKValue: (state) => (formId) => {
            if (!formId) {
                return;
            }
            return state.forms[formId].formElements.find((element) => element.id === 'district').state.value;
        },
        getFieldValue: (state) => (formId, fieldId) => {
            const form = state.forms[formId];
            const formElements = form ? form.formElements : state.forms.formElements;
            const formElement = formElements.find((element) => element.id === fieldId);
            return formElement ? formElement.state.value : null;
        },
        phoneNumber: (state) => getPhoneNumber(state.forms.shippingAddress.formElements),
        getFormsAsJSON: (state) => {
            const json = {
                shippingAddress: getElementsAsJSON(state.forms.shippingAddress.formElements),
                billingAddress: getElementsAsJSON(state.forms.billingAddress.formElements),
                companydetails: getElementsAsJSON(state.forms.companydetails.formElements)
            };
            json.shippingAddress.phone = getPhoneNumber(state.forms.shippingAddress.formElements);
            const sameAsDeliveryAddress = state.forms.billingAddress.formElements[0];
            if (sameAsDeliveryAddress && sameAsDeliveryAddress.state.value) {
                Object.keys(json.billingAddress).forEach(function (key) {
                    if (key in json.shippingAddress) {
                        json.billingAddress[key] = json.shippingAddress[key];
                    }
                    if (key === 'saveAddress') {
                        json.billingAddress[key] = false;
                    }
                });
            }

            return json;
        },
        getStateOptions: (state) => {
            const stateAttribute = state.forms.shippingAddress.formElements.find((element) => element.id === 'state');
            if (!stateAttribute) {
                return;
            }

            if (!stateAttribute.attributes.options.length) {
                return;
            }

            return stateAttribute.attributes.options.reduce((acc, option) => {
                if (option.id) {
                    acc.region[option.id] = option.label;
                }
                return acc;
            }, {region: {}});
        },
        getFormAsJSON: (state) => (formId) => {
            const form = state.forms[formId];
            if (!form) {
                return;
            }

            return getElementsAsJSON(form.formElements);
        },
        isFormValidatedAndInvalid: (state) => (formId) => {
            const form = state.forms[formId];
            const formElements = form ? form.formElements : state.forms.formElements;
            return formElements.some((element) => element.validationErrors.length);
        },
        showPhoneRow: (state) =>
            !!state.forms.shippingAddress.formElements.find((element) => element.id === 'phoneNumberOptions'),
        validateRGCInput: (state) => (formId) => {
            const form = state.forms[formId];
            const formElements = form ? form.formElements : state.forms.formElements;
            return validateRGCInput(formElements);
        },
        getPlaceholderPhoneNumber: (state) => getPlaceholderPhoneNumber(state.forms.shippingAddress.formElements)
    },
    actions: {
        postForms({state, commit, getters, rootGetters}, dontSaveAddresses) {
            const forms = getters.getFormsAsJSON;
            forms.pickupPerson = rootGetters['delivery/pickupPerson'];
            forms.dHLPostNummer = rootGetters['delivery/postnummer'];
            forms.pickPostUserID = rootGetters['delivery/pickPost'];
            forms.dontSaveAddresses = dontSaveAddresses || false;

            return axios({
                method: 'post',
                url: state.forms.attributes.actionURL,
                data: forms,
                headers: {'Content-Type': 'application/json'}
            }).then((result) => {
                if (result && result.data && !('skipUpdateFormData' in result.data)) {
                    commit('updateFormData', result.data);
                }
                return true;
            });
        },
        getAddressInfo({state, commit}, value) {
            const url = `${state.ssnField.actionURL}/?ssn=${value}`;
            return axios({
                method: 'post',
                url: url,
                data: {},
                headers: {'Content-Type': 'application/json'}
            }).then((result) => {
                if (result.data.result) {
                    const data = JSON.parse(result.data.result);
                    commit('updateFieldsFromSSN', data);
                }
            });
        },
        getAddressFromRGC({state, commit}, formId) {
            const appendParamsToUrl = require('../../../../modules/util/appendParamsToUrl');
            const form = state.forms[formId];
            const formElements = form ? form.formElements : state.forms.formElements;
            const houseNumberField = formElements.find((el) => el.id === 'houseNumber');
            const zipCodeField = formElements.find((el) => el.id === 'postalCode');
            const formPrefix =
                formId === 'shippingAddress'
                    ? 'dwfrm_singleshipping_shippingAddress_addressFields_'
                    : 'dwfrm_billing_billingAddress_addressFields_';
            const url = appendParamsToUrl(Urls.rgcRetrieveAddress, {
                postalCode: zipCodeField.state.value.replace(/\s/g, ''),
                houseNumber: houseNumberField.state.value,
                format: 'ajax',
                formPrefix: formPrefix
            });
            return axios({
                method: 'get',
                url: url,
                data: {},
                headers: {'Content-Type': 'application/json'}
            }).then((result) => {
                if (result.data) {
                    commit('updateFieldsFromRGC', {data: result.data, formId: formId});
                }
            });
        },
        submitMemberDetails({state, rootState, commit, getters, rootGetters}) {
            const billingAddressId = rootState.addressBook.selectedAddresses.selectedBillingAddressId;
            const phone = getPhoneNumber(state.forms.shippingAddress.formElements);
            const savePhoneNumber = getters.getFieldValue('shippingAddress', 'savePhoneNumber');
            const selectedPhoneOption = getters.getFieldValue('shippingAddress', 'phoneNumberOptions');
            const vat =
                getters.getFieldValue('billingAddress', 'btwVatNumber') ||
                getters.getFieldValue('billingAddress', 'nifNumber');
            const useSameAsShipping = getters.getFieldValue('billingAddress', 'useSameAsShipping');
            const data = {billingAddressId, phone, vat, useSameAsShipping, savePhoneNumber};
            const address = rootGetters['addressBook/getAddressById'](billingAddressId);
            if (address && address.temporary) {
                data.tempAddress = address.fieldValues;
            }
            data.pickupPerson = rootGetters['delivery/pickupPerson'];
            data.dHLPostNummer = rootGetters['delivery/postnummer'];
            data.pickPostUserID = rootGetters['delivery/pickPost'];
            return axios({
                method: 'post',
                url: state.submitDetailsURL,
                data,
                headers: {'Content-Type': 'application/json'}
            }).then((result) => {
                if (result.data) {
                    commit('updateDetails', result.data);
                }
                commit('setEditMode', false);
                if (selectedPhoneOption) {
                    commit('updateFieldValue', {
                        formId: 'shippingAddress',
                        fieldId: 'phoneNumberOptions',
                        value: selectedPhoneOption
                    });
                }
                return true;
            });
        },
        submitGuestDetails({state, rootState, commit, dispatch, getters}) {
            const isUSUser = true;
            const phone = getPhoneNumber(state.forms.shippingAddress.formElements);
            const useSameAsShipping = getters.getFieldValue('billingAddress', 'useSameAsShipping');
            const savePhoneNumber = getters.getFieldValue('shippingAddress', 'savePhoneNumber');
            const forms = getters.getFormsAsJSON;
            const data = {...forms, phone, useSameAsShipping, savePhoneNumber, isUSUser};

            return axios({
                method: 'post',
                url: state.forms.attributes.actionURL,
                data: data,
                headers: {'Content-Type': 'application/json'}
            }).then((result) => {
                if (result.data.addressValidationError) {
                    commit('error/setErrorMessage', result.data.addressValidationError, {root: true});
                } else {
                    rootState.delivery.deliverySummary.editMode = false;
                    commit('updateDetails', result.data.details);
                    commit('delivery/setDeliveryState', result.data.delivery, {root: true});
                    commit('orderSummary/setOrderSummaryData', result.data.orderSummary, {root: true});
                    commit('addressBook/updateAddresses', result.data.addressBook, {root: true});
                    commit('setEditMode', false);
                    commit('error/setErrorMessage', null, {root: true});

                    const addressObject = result.data.addressObject;
                    for (const adresses in addressObject) {
                        const addressId = addressObject[adresses].ID;
                        const type = addressObject[adresses].type;
                        commit('addressBook/selectAddress', {type, addressId}, {root: true});
                        dispatch('addressBook/updateSelectedAddress', {type, addressId}, {root: true});
                    }
                }
                return true;
            });
        },
        saveAddressesToForms({state, getters}) {
            return axios({
                method: 'post',
                url: state.saveAddressesToFormsURL,
                data: getters.getFormsAsJSON,
                headers: {'Content-Type': 'application/json'}
            });
        },
        setStateCodeByPostal({state, commit, getters}, addressType) {
            if (state.countryCode !== 'US') {
                return;
            }
            const postalCode =
                addressType === 'shippingAddress' ? getters.shippingPostalCodeValue : getters.billingPostalCodeValue;

            return getStateUSCode(postalCode).then((stateUS) => {
                if (stateUS) {
                    commit('updateFieldValue', {
                        formId: addressType,
                        fieldId: 'stateUS',
                        value: stateUS
                    });
                }
            });
        },
        setCityHK({commit}, formId) {
            if (!formId) {
                return;
            }
            return axios({
                method: 'get',
                url: Urls.getTowns,
                data: {},
                headers: {'Content-Type': 'application/json'}
            }).then((response) => {
                commit('setFieldValue', {
                    formId: formId,
                    fieldId: 'city',
                    value: response.data.result.towns
                });
            });
        },
        setDistrictHK({commit, getters}, formId) {
            if (!formId) {
                return;
            }
            const appendParamsToUrl = require('../../../../modules/util/appendParamsToUrl');
            const getCityHKValue = getters.getCityHKValue(formId);
            const url = appendParamsToUrl(Urls.getDistrictsForTown, {
                town: getCityHKValue
            });
            return axios({
                method: 'get',
                url: url,
                data: {},
                headers: {'Content-Type': 'application/json'}
            }).then((response) => {
                commit('setFieldValue', {
                    formId: formId,
                    fieldId: 'district',
                    value: response.data.result.districts
                });
            });
        },
        setSubdistrictHK({commit, getters}, formId) {
            if (!formId) {
                return;
            }
            const appendParamsToUrl = require('../../../../modules/util/appendParamsToUrl');
            const getDistrictHKValue = getters.getDistrictHKValue(formId);
            const url = appendParamsToUrl(Urls.getSubdistrictsForDistrict, {
                district: getDistrictHKValue
            });
            return axios({
                method: 'get',
                url: url,
                data: {},
                headers: {'Content-Type': 'application/json'}
            }).then((response) => {
                commit('setFieldValue', {
                    formId: formId,
                    fieldId: 'subdistrict',
                    value: response.data.result.subdistricts
                });
            });
        }
    },
    mutations: {
        updateFieldsFromSSN(state, data) {
            const shippingElements = state.forms.shippingAddress.formElements;
            Object.entries(data).forEach(([key, value]) => {
                const fieldId = ssnMapping[key];
                if (!value || !fieldId) {
                    return;
                }
                const field = shippingElements.find((element) => element.id === fieldId);
                if (field) {
                    field.state.value = value;
                }
            });
        },
        updateFieldsFromRGC(state, args) {
            const {data, formId} = args;
            const formElements = state.forms[formId] ? state.forms[formId].formElements : state.forms.formElements;
            Object.entries(data.data).forEach(([key, value]) => {
                const fieldId = rgcMapping[key];
                if (!value || !fieldId) {
                    return;
                }
                const field = formElements.find((element) => element.id === fieldId);
                if (field) {
                    field.state.value = value;
                }
            });
        },
        updateDetails(state, newState) {
            Object.assign(state, newState);
        },
        setFieldValue(state, args) {
            const {formId, fieldId, value} = args;
            const formElements = state.forms[formId] ? state.forms[formId].formElements : state.forms.formElements;
            const element = formElements.find((element) => element.id === fieldId);
            if (!element) {
                return;
            }
            if (element.attributes.options && element.attributes.options.length !== 0) {
                element.attributes.options.splice(0, element.attributes.options.length);
            }
            value.forEach(function (option) {
                element.attributes.options.push({
                    id: option.value,
                    htmlValue: option.value,
                    label: option.text
                });
            });
            element.state.selectedOption = element.attributes.options[0];
            element.state.__ob__.dep.notify(); // if the initial value === trimmed value the components won't be notified
        },
        updateFieldValue(state, args) {
            const {formId, fieldId, value} = args;
            const formElements = state.forms[formId] ? state.forms[formId].formElements : state.forms.formElements;
            const element = formElements.find((element) => element.id === fieldId);
            if (!element) {
                return;
            }
            element.state.value = typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : value;
            if (element.attributes.options && element.attributes.options.length) {
                element.state.selectedOption = element.attributes.options.find((option) => option.id === value);
            }
            element.state.__ob__.dep.notify(); // if the initial value === trimmed value the components won't be notified
            element.softValidationErrors = softValidateElementAttributes(element);
            element.validationErrors = validateElementInFormContext(element, formElements);
            updatePhoneErrors(element, formElements);
            if (element.validationErrors.length > 0) {
                this.commit('analytics/handleCheckoutFieldError', {section: 'details', message: element.validationErrors[0]});
            }
        },
        addValidationError(state, args) {
            const {formId, fieldId, error} = args;
            const formElements = state.forms[formId] ? state.forms[formId].formElements : state.forms.formElements;
            const element = formElements.find((element) => element.id === fieldId);
            if (!element) {
                return;
            }
            element.validationErrors.push(error);
        },
        removeValidationError(state, args) {
            const {formId, fieldId, error} = args;
            const formElements = state.forms[formId] ? state.forms[formId].formElements : state.forms.formElements;
            const element = formElements.find((element) => element.id === fieldId);
            if (!element) {
                return;
            }
            element.validationErrors = element.validationErrors.filter((err) => err !== error);
        },
        validateForm(state, formId) {
            const elements = state.forms[formId].formElements;
            elements.forEach((element) => {
                element.softValidationErrors = softValidateElementAttributes(element);
                element.validationErrors = validateElementInFormContext(element, elements);
            });
        },
        updatesameAsDeliveryAddress(state, value) {
            state.sameAsDeliveryAddress.state.value = value;
        },
        updateFormData(state, forms) {
            forms.shippingAddress.formElements.forEach((element, i) => {
                state.forms.shippingAddress.formElements[i].validationErrors = element.validationErrors;
            });
            forms.shippingAddress.formElements.forEach((element, i) => {
                state.forms.shippingAddress.formElements[i].softValidationErrors = element.softValidationErrors;
            });
            forms.billingAddress.formElements.forEach((element, i) => {
                state.forms.billingAddress.formElements[i].validationErrors = element.validationErrors;
            });
            forms.billingAddress.formElements.forEach((element, i) => {
                state.forms.billingAddress.formElements[i].softValidationErrors = element.softValidationErrors;
            });
            if (forms.companydetails) {
                forms.companydetails.formElements.forEach((element, i) => {
                    state.forms.companydetails.formElements[i].validationErrors = element.validationErrors;
                });
            }
        },
        copyShippingToBillingAddress(state) {
            const billingElements = state.forms.billingAddress.formElements;
            const shippingElements = state.forms.shippingAddress.formElements;

            billingElements.forEach((element) => {
                const shippingElement = shippingElements.find((shippingElemenet) => shippingElemenet.id === element.id);
                if (shippingElement) {
                    element.state.value = shippingElement.state.value;
                }
            });
        },
        setSelectedBillingAddressId(state, id) {
            state.selectedBillingAddressId = id;
        },
        clearElements(state, {formId, elementIds}) {
            const form = state.forms[formId];
            if (!form) {
                return;
            }

            form.formElements.forEach((element) => {
                if (!elementIds.includes(element.id) || element.attributes.readOnly) {
                    return;
                }
                if (typeof element.state.value === 'boolean') {
                    element.state.value = false;
                    return;
                }
                element.state.value = '';
                element.state.htmlValue = '';
                element.validationErrors = [];
                element.softValidationErrors = [];
                if (element.attributes.options && element.attributes.options.length) {
                    element.state.selectedOption = element.attributes.options[0];
                }
            });
        },
        setEditMode(state, value) {
            state.editMode = value;
        },
        setShowEditModeError(state, value) {
            state.showEditModeError = value;
        }
    }
});

function validateElementInFormContext(element, formElements) {
    if (element.id === 'phone' || element.id === 'phoneNumberOptions') {
        return validatePhoneGroupElements(element, formElements);
    }

    return validateElement(element);
}

function validatePhoneGroupElements(element, formElements) {
    if (element.id === 'phone') {
        return validatePhoneElement(element, formElements);
    }

    return [];
}

function updatePhoneErrors(element, formElements) {
    if (element.id === 'phoneNumberOptions' && element.state.value === 'savedPhoneNumber') {
        const phone = formElements.find((el) => el.id === 'phone');
        if (phone) {
            phone.validationErrors = [];
        }
    }
}

function validatePhoneElement(phone, formElements) {
    const phoneOptions = formElements.find((el) => el.id === 'phoneNumberOptions');
    if (!phoneOptions || phoneOptions.state.value !== 'savedPhoneNumber') {
        return validateElement(phone);
    }

    return [];
}

function getPhoneNumber(formElements) {
    const phoneField = formElements.find((el) => el.id === 'phone');
    const phoneOptions = formElements.find((el) => el.id === 'phoneNumberOptions');
    if (!phoneOptions || phoneOptions.state.value !== 'savedPhoneNumber') {
        return phoneField.state.value;
    }

    return phoneOptions.attributes.options[0].label;
}
function getPlaceholderPhoneNumber(formElements) {
    const phoneField = formElements.find((el) => el.id === 'phone');
    const placeholder = phoneField.attributes.placeholder;
    const plusIndex = placeholder.indexOf('+');

    return placeholder.substring(plusIndex);
}
function validateRGCInput(formElements) {
    const houseNumberField = formElements.find((el) => el.id === 'houseNumber');
    const zipCodeField = formElements.find((el) => el.id === 'postalCode');
    if (houseNumberField && houseNumberField.state.value != '' && zipCodeField && zipCodeField.state.value != '') {
        return true;
    }
}
function getElementsAsJSON(elements) {
    const json = {};
    elements.forEach((element) => {
        json[element.id] = element.state.value;
    });

    return json;
}
function getStateUSCode(postalCode) {
    const country = require('../../../../modules/util/getCountryFromLocaleCookie')();
    const geocoder = new google.maps.Geocoder();
    let stateUSCode = null;

    return new Promise(function (resolve) {
        geocoder.geocode(
            {
                componentRestrictions: {
                    postalCode: postalCode,
                    country: country
                }
            },
            function (results, status) {
                if (status === 'OK') {
                    const addressComponents = results[0].address_components.filter(function (addressComponent) {
                        return addressComponent.types[0] === 'administrative_area_level_1';
                    });

                    stateUSCode =
                        addressComponents && addressComponents[0] ? addressComponents[0].short_name.toUpperCase() : '';
                    resolve(stateUSCode);
                } else {
                    resolve(stateUSCode);
                }
            }
        );
    });
}
